import {
  slab, roof, wallX, wallZ, goal, shard, checkpoint, sign, neon, lift, edgeLights,
} from '../kit.js';

const signal = (x, y, z, yaw = 0) => [
  shard(x, y + 1.6, z), checkpoint(x, y, z, yaw),
  neon(x, y + 0.07, z, [2, 0.1, 2], 'lime'),
];

// Tile boundaries become walls only where the corridor has no neighbor.
// Open endpoints connect to the chambers; galleries omit tall side walls.
const corridor = (cells, y, { openings = [], galleries = [], unroofed = [] } = {}) => {
  const occupied = new Set(cells.map(([x, z]) => `${x},${z}`));
  const open = new Set(openings.map(([x, z]) => `${x},${z}`));
  const gallery = new Set(galleries.map(([x, z]) => `${x},${z}`));
  const uncovered = new Set(unroofed.map(([x, z]) => `${x},${z}`));
  return cells.flatMap(([tx, tz]) => {
    const key = `${tx},${tz}`, x = tx * 12, z = tz * 12;
    const h = gallery.has(key) ? 0.7 : 10;
    const out = [slab(x, y, z, 12, 12), neon(x, y + 0.07, z, [2, 0.1, 2], 'amber')];
    if (!gallery.has(key) && !uncovered.has(key)) out.push(roof(x, y + 10, z, 12, 12, 'trim'));
    for (const dx of [-1, 1]) if (!occupied.has(`${tx + dx},${tz}`) && !open.has(`${tx + dx},${tz}`)) {
      out.push(wallX(x + dx * 6.6, y, z, 12, h, 'plate'));
    }
    for (const dz of [-1, 1]) if (!occupied.has(`${tx},${tz + dz}`) && !open.has(`${tx},${tz + dz}`)) {
      out.push(wallZ(x, y, z + dz * 6.6, 12, h, 'plate'));
    }
    return out;
  });
};

// Partition a chamber wall around real door openings at different floors.
// The rectangles are solid authored boxes, not a visual-only doorway.
const partition = (axis, fixed, start, end, bottom, top, holes = []) => {
  const along = [...new Set([start, end, ...holes.flatMap(h => h.slice(0, 2))])].sort((a, b) => a - b);
  const heights = [...new Set([bottom, top, ...holes.flatMap(h => h.slice(2))])].sort((a, b) => a - b);
  const out = [];
  for (let i = 1; i < along.length; i++) for (let j = 1; j < heights.length; j++) {
    const a = along[i - 1], b = along[i], lo = heights[j - 1], hi = heights[j];
    const mid = (a + b) / 2, y = (lo + hi) / 2;
    if (holes.some(h => mid > h[0] && mid < h[1] && y > h[2] && y < h[3])) continue;
    out.push(axis === 'x' ? wallX(fixed, lo, mid, b - a, hi - lo, 'plate')
      : wallZ(mid, lo, fixed, b - a, hi - lo, 'plate'));
  }
  return out;
};

const arrival = [[0, 4], [0, 3], [-1, 3], [-2, 3], [-2, 2], [-2, 1], [-1, 1], [0, 1], [0, 0]];
const underworks = [[1, -9], [2, -9], [3, -9], [4, -9], [4, -8], [4, -7],
  [5, -7], [6, -7], [7, -7], [7, -6], [7, -5], [8, -5], [8, -4]];
const upper = [[8, 2], [8, 3], [7, 3], [6, 3], [5, 3], [4, 3], [3, 3],
  [2, 3], [1, 3], [0, 3], [-1, 3], [-2, 3], [-3, 3], [-4, 3], [-4, 2], [-5, 2], [-6, 2], [-6, 1]];

export default {
  id: 'crossflow',
  name: 'CROSSFLOW',
  blurb: 'Seven signals. Four flights. The facility folds around you.',
  unlockAfter: 'switchback',
  mode: { type: 'collect', par: 300 },
  redline: { seconds: 270 },
  spawn: { p: [0, 24.4, 48], yaw: 0 },
  killY: -100,
  env: {
    fog: 0x10171e, fogDensity: 0.0012,
    sky: { zenith: 0x060b12, horizon: 0x263946, glow: 0x255878, aurora: 0x426c76 },
    floorY: -180,
  },
  objects: [
    // INTAKE. The entry doubles back inside the south end of the flight bay.
    ...corridor(arrival, 24, { openings: [[0, -1]] }),
    sign(0, 27.6, 43, 0, ['CROSSFLOW', 'SEVEN SIGNALS. FOUR PORTAL FLIGHTS.', 'FOLLOW THE AMBER FLOOR']),
    sign(-24, 27.6, 17, 0, ['01 // TRANSFER BAY', 'PREPARE THE WALL EXIT BEFORE YOU DROP']),
    slab(0, 24, -12, 20, 12), ...edgeLights(0, 24, -12, 20, 12),
    ...signal(0, 24, -12),
    wallZ(0, 28, -5.4, 20, 16, 'panel'),
    sign(0, 27.6, -16, 0, ['EXIT: BLUE WALL BEHIND YOU', 'ENTRY: DEEP FLOOR AHEAD', 'BANK RIGHT ON THE WAY OUT']),

    // TRANSFER BAY. The landing sits off the gun's straight-ahead line.
    slab(0, -44, -42, 72, 192), roof(0, 76, -42, 72, 192, 'trim'),
    ...partition('x', -36.6, -138, 54, -44, 76, [[30, 42, 64, 76]]),
    ...partition('x', 36.6, -138, 54, -44, 76, [[-114, -102, -8, 4], [30, 42, 64, 76]]),
    wallZ(0, -44, -138.6, 74.4, 120, 'plate'),
    wallZ(0, -44, 54.6, 74.4, 120, 'plate'),
    slab(0, -40, -30, 24, 24, 'panel'),
    wallX(-12.6, -40, -30, 24, 64, 'plate'),
    wallX(12.6, -40, -30, 24, 64, 'plate'),
    wallZ(0, -40, -17.4, 24, 64, 'plate'),
    wallZ(0, -40, -42.6, 24, 44, 'plate'),
    neon(-11.8, -8, -30, [0.12, 60, 0.12], 'cyan'),
    neon(11.8, -8, -30, [0.12, 60, 0.12], 'cyan'),
    slab(12, -8, -116, 20, 44), ...edgeLights(12, -8, -116, 20, 44, 'lime'),
    ...signal(12, -8, -108, -90),
    sign(12, -4.4, -116, 180, ['02 // OFFSET LANDING', 'FOLLOW AMBER INTO THE SERVICE LOOP'], '#9dff3c'),

    // UNDERWORKS. Turns hide the next launch while the corridor changes sides.
    ...corridor(underworks, -8, { openings: [[1, -10]], unroofed: [[8, -4]] }),
    sign(48, -4.4, -91, 180, ['SERVICE LOOP', 'RETURN TOWER AHEAD'], '#ffb020'),
    sign(84, -4.4, -64, 180, ['03 // RETURN TOWER', 'LIFT AT THE END OF THE CORRIDOR'], '#ffb020'),

    // RETURN TOWER. Launch vertically, then steer onto the upper gallery.
    slab(90, -40, -27, 60, 138, 'panel'), roof(90, 96, -27, 60, 138, 'trim'),
    ...partition('x', 59.4, -96, 42, -40, 96, [[-90, -78, -8, 4], [30, 42, 64, 76]]),
    wallX(120.6, -40, -27, 138, 136, 'plate'),
    wallZ(90, -40, -96.6, 62.4, 136, 'plate'),
    wallZ(90, -40, 42.6, 62.4, 136, 'plate'),
    lift(96, -8, -48, 36, 2.3),
    slab(96, 24, -36, 24, 12), ...edgeLights(96, 24, -36, 24, 12),
    ...signal(96, 24, -36, 180),
    sign(96, 27.6, -32, 180, ['TAG THE SMALL BLUE RECEIVER', 'DROP TO THE DEEP FLOOR', 'RISE, THEN STEER TO THE GREEN LEDGE']),
    slab(104, 12, 4, 6, 6, 'panel'), ...edgeLights(104, 12, 4, 6, 6),
    slab(96, 64, 24, 36, 12), ...edgeLights(96, 64, 24, 36, 12, 'lime'),
    checkpoint(96, 64, 24, 180),
    neon(119.8, 28, -27, [0.14, 126, 0.14], 'cyan'),

    // OVERLOOK. The lower entry maze is visible from this upper crossing.
    ...corridor(upper, 64, { openings: [[8, 1], [-6, 0]], unroofed: [[8, 2]], galleries: [[2, 3], [1, 3], [0, 3], [-1, 3], [-2, 3]] }),
    ...signal(0, 64, 36, 90),
    sign(12, 67.6, 36, 90, ['04 // OVERLOOK', 'YOUR ENTRANCE IS BELOW', 'EXHAUST WING TO THE WEST'], '#ffb020'),
    sign(-48, 67.6, 28, 0, ['EXHAUST', 'TWO CONNECTED MOVES TO THE EXIT'], '#ff2fd0'),

    // EXHAUST I. An offset wall flight ends at a lower transfer perch.
    slab(-90, -32, -39, 84, 162, 'panel'), roof(-90, 148, -39, 84, 162, 'trim'),
    wallX(-132.6, -32, -39, 162, 180, 'plate'),
    ...partition('x', -47.4, -120, 42, -32, 148, [[18, 42, 64, 76]]),
    wallZ(-90, -32, -120.6, 86.4, 180, 'plate'),
    wallZ(-90, -32, 42.6, 86.4, 180, 'plate'),
    slab(-72, 64, 0, 20, 24), ...edgeLights(-72, 64, 0, 20, 24, 'magenta'),
    ...signal(-72, 64, 0),
    wallZ(-72, 72, 5.4, 20, 16, 'panel'),
    sign(-72, 67.6, -8, 0, ['05 // EXHAUST TRANSFER', 'WALL EXIT BEHIND. PIT AHEAD.', 'BANK LEFT TO THE TRANSFER PERCH'], '#ff2fd0'),
    slab(-72, 0, -24, 20, 24, 'panel'),
    wallX(-82.6, 0, -24, 24, 64, 'plate'),
    wallX(-61.4, 0, -24, 24, 64, 'plate'),
    wallZ(-72, 0, -11.4, 20, 64, 'plate'),
    wallZ(-72, 0, -36.6, 20, 48, 'plate'),
    slab(-96, 32, -98, 24, 44), ...edgeLights(-96, 32, -98, 24, 44, 'lime'),
    ...signal(-96, 32, -90, 90),
    sign(-96, 35.6, -84, 180, ['06 // TRANSFER PERCH', 'NEW EXIT: SMALL BLUE FLOOR', 'DROP FROM THE LIT EDGE'], '#ffb020'),

    neon(-96, 32.08, -76, [8, 0.12, 0.2], 'amber'),

    // EXHAUST II. Rebuild the pair for a vertical return over the first pit.
    slab(-108, 16, -64, 6, 6, 'panel'), ...edgeLights(-108, 16, -64, 6, 6),
    slab(-108, 64, -36, 36, 16), ...edgeLights(-108, 64, -36, 36, 16, 'lime'),
    checkpoint(-108, 64, -36, 180),
    slab(-108, 64, 0, 12, 56), ...edgeLights(-108, 64, 0, 12, 56, 'lime'),
    ...signal(-108, 64, 12, 180),
    sign(-108, 67.6, 4, 180, ['07 // ABOVE THE EXHAUST', 'SEVEN SIGNALS. ONE WAY HOME.'], '#9dff3c'),
    goal(-108, 64, 24, 180),
  ],
};
